<template>
  <div class="overview-container">

    <!-- ── HEADER ── -->
    <div class="page-header">
      <div class="breadcrumb">ຂໍ້ມູນໂຮງຮຽນ / <span>ພາບລວມ</span></div>
      <div class="header-row">
        <div>
          <h1><i class="fas fa-chart-bar"></i> ພາບລວມຂໍ້ມູນໂຮງຮຽນ</h1>
          <p>ສັງລວມຂໍ້ມູນທຸກດ້ານ — ຕາມໂຮງຮຽນ, ເມືອງ ຫຼື ແຂວງ</p>
        </div>
      </div>
    </div>

    <!-- ── FILTER BAR ── -->
    <div class="filter-card">
      <div class="filter-row">
        <!-- Province -->
        <div class="filter-group">
          <label><i class="fas fa-map-marker-alt"></i> ແຂວງ</label>
          <div class="sel-wrap">
            <select v-model="filter.prov_id" @change="onProvChange">
              <option value="">-- ທຸກແຂວງ --</option>
              <option v-for="p in provinces" :key="p.value" :value="p.value">{{ p.text }}</option>
            </select>
            <i v-if="provLoading" class="fas fa-spinner fa-spin sel-spin"></i>
          </div>
        </div>

        <!-- District -->
        <div class="filter-group">
          <label><i class="fas fa-city"></i> ເມືອງ</label>
          <div class="sel-wrap">
            <select v-model="filter.district_id" @change="onDistChange"
                    :disabled="!filter.prov_id || distLoading">
              <option value="">-- ທຸກເມືອງ --</option>
              <option v-for="d in districts" :key="d.value" :value="d.value">{{ d.text }}</option>
            </select>
            <i v-if="distLoading" class="fas fa-spinner fa-spin sel-spin"></i>
          </div>
        </div>

        <!-- Academic year -->
        <div class="filter-group filter-accd">
          <label><i class="fas fa-calendar-alt"></i> ສົກຮຽນ</label>
          <div class="sel-wrap">
            <select v-model="filter.academic_year">
              <option value="">-- ທຸກສົກຮຽນ --</option>
              <option v-for="a in academicYears" :key="a" :value="a">{{ a }}</option>
            </select>
            <i v-if="accdLoading" class="fas fa-spinner fa-spin sel-spin"></i>
          </div>
        </div>

        <!-- Education Level -->
        <div class="filter-group filter-level">
          <label><i class="fas fa-layer-group"></i> ຂັ້ນການສຶກສາ</label>
          <div class="sel-wrap">
            <select v-model="filter.level_id" @change="onLevelChange">
              <option value="">-- ທຸກຂັ້ນ --</option>
              <option v-for="l in LEVELS" :key="l.id" :value="l.id">{{ l.name }}</option>
            </select>
          </div>
        </div>

        <!-- School searchable -->
        <div class="filter-group filter-school">
          <label><i class="fas fa-school"></i> ໂຮງຮຽນ</label>
          <div class="school-pick-wrap" :class="{ disabled: !filter.district_id }">
            <i class="fas fa-school sp-icon"></i>
            <input ref="spInput" v-model="schoolSearch"
                   :placeholder="schoolPickPlaceholder"
                   :disabled="!filter.district_id || schoolLoading"
                   autocomplete="off"
                   @focus="spOpen = true" @blur="onSpBlur" />
            <button v-if="filter.school_id" type="button" class="sp-clear"
                    @mousedown.prevent="clearSchool">
              <i class="fas fa-times-circle"></i>
            </button>
            <i v-else class="fas fa-chevron-down sp-arr"></i>
            <ul v-if="spOpen && !schoolLoading" class="sp-list">
              <li v-if="filteredSchools.length === 0" class="sp-empty">ບໍ່ພົບໂຮງຮຽນ</li>
              <li v-for="s in filteredSchools" :key="s.school_id"
                  :class="{ active: s.school_id === filter.school_id }"
                  @mousedown.prevent="pickSchool(s)">
                <i class="fas fa-school sp-li-icon"></i>
                <div class="sp-school-info">
                  <span class="sp-school-name">{{ s.school_name_lao }}</span>
                  <div class="sp-school-meta">
                    <span class="sp-emis">{{ s.school_id }}</span>
                    <span v-if="s.school_level_lao" class="sp-level">{{ s.school_level_lao }}</span>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <button class="btn-search" @click="loadOverview" :disabled="loading">
          <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-search'"></i>
          ຄົ້ນຫາ
        </button>
        <button class="btn-reset" @click="resetFilter">
          <i class="fas fa-undo"></i>
        </button>
      </div>
    </div>

    <!-- ── LOADING ── -->
    <div v-if="loading" class="loading-box">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
      <p>ກຳລັງດຶງຂໍ້ມູນ...</p>
    </div>

    <template v-else-if="schools.length > 0">

      <!-- ── SUMMARY STATS ── -->
      <div class="stats-row">
        <div class="stat-card stat-total">
          <div class="stat-icon"><i class="fas fa-school"></i></div>
          <div><div class="stat-num">{{ schools.length }}</div><div class="stat-lbl">ໂຮງຮຽນທັງໝົດ</div></div>
        </div>
        <div class="stat-card stat-stu">
          <div class="stat-icon"><i class="fas fa-user-graduate"></i></div>
          <div><div class="stat-num">{{ totalStudents.toLocaleString() }}</div><div class="stat-lbl">ນັກຮຽນທັງໝົດ</div></div>
        </div>
        <div class="stat-card stat-tea">
          <div class="stat-icon"><i class="fas fa-chalkboard-teacher"></i></div>
          <div><div class="stat-num">{{ totalTeachers }}</div><div class="stat-lbl">ຄູສອນທັງໝົດ</div></div>
        </div>
        <div class="stat-card stat-room">
          <div class="stat-icon"><i class="fas fa-door-open"></i></div>
          <div><div class="stat-num">{{ totalRooms }}</div><div class="stat-lbl">ຫ້ອງຮຽນທັງໝົດ</div></div>
        </div>
        <div v-for="sec in SECTIONS" :key="sec.key" class="stat-card stat-sec">
          <div class="stat-icon sec-icon" :style="{ background: sec.color + '22', color: sec.color }">
            <i :class="sec.icon"></i>
          </div>
          <div>
            <div class="stat-num">{{ sectionDoneCount(sec.key) }}<span class="stat-of">/{{ schools.length }}</span></div>
            <div class="stat-lbl">{{ sec.shortLabel }}</div>
          </div>
        </div>
      </div>

      <!-- ── VIEW: SINGLE SCHOOL DETAIL ── -->
      <template v-if="filter.school_id && detail">
        <div class="detail-school-bar">
          <i class="fas fa-school"></i>
          <span>{{ detail.school?.school_name_lao }}</span>
          <span class="detail-loc">{{ detail.school?.district_name_lao }} / {{ detail.school?.prov_name }}</span>
        </div>

        <div class="detail-stats-row">
          <div class="ds-card">
            <i class="fas fa-users"></i>
            <div class="ds-num">{{ detail.stats.students }}</div>
            <div class="ds-lbl">ນັກຮຽນທັງໝົດ</div>
          </div>
          <div class="ds-card">
            <i class="fas fa-mars"></i>
            <div class="ds-num">{{ detail.stats.male }}</div>
            <div class="ds-lbl">ນັກຮຽນຊາຍ</div>
          </div>
          <div class="ds-card">
            <i class="fas fa-venus"></i>
            <div class="ds-num">{{ detail.stats.female }}</div>
            <div class="ds-lbl">ນັກຮຽນຍິງ</div>
          </div>
          <div class="ds-card">
            <i class="fas fa-chalkboard-teacher"></i>
            <div class="ds-num">{{ detail.stats.teachers }}</div>
            <div class="ds-lbl">ຄູສອນ</div>
          </div>
          <div class="ds-card">
            <i class="fas fa-door-open"></i>
            <div class="ds-num">{{ schools[0]?.rooms || 0 }}</div>
            <div class="ds-lbl">ຫ້ອງຮຽນ</div>
          </div>
          <div v-if="filter.academic_year" class="ds-card ds-accd">
            <i class="fas fa-calendar-alt"></i>
            <div class="ds-num ds-accd-val">{{ filter.academic_year }}</div>
            <div class="ds-lbl">ສົກຮຽນ</div>
          </div>
        </div>

        <div class="section-grid">
          <div v-for="sec in SECTIONS" :key="sec.key" class="sec-card"
               :class="{ 'sec-done': hasSectionData(sec.key), 'sec-missing': !hasSectionData(sec.key) }">
            <div class="sec-header" :style="{ background: sec.color }">
              <i :class="sec.icon"></i>
              <span>{{ sec.label }}</span>
              <i class="fas sec-status-icon"
                 :class="hasSectionData(sec.key) ? 'fa-check-circle' : 'fa-exclamation-circle'"></i>
            </div>
            <div class="sec-body">
              <!-- Section-specific key metrics -->
              <template v-if="hasSectionData(sec.key)">
                <div class="sec-metrics">
                  <template v-if="sec.key === 'profile'">
                    <div class="metric"><span>ລະດັບ:</span> {{ detail.sections.profile?.school_level_lao || '—' }}</div>
                    <div class="metric"><span>ປະເພດ:</span> {{ detail.sections.profile?.schooltype || '—' }}</div>
                    <div class="metric"><span>ສົກຮຽນ:</span> {{ detail.sections.profile?.academic_year || '—' }}</div>
                  </template>
                  <template v-if="sec.key === 'infrastructure'">
                    <div class="metric"><span>ຫ້ອງຮຽນ:</span> {{ detail.sections.infrastructure?.total_classrooms || '—' }}</div>
                    <div class="metric"><span>ຫ້ອງໃຊ້ໄດ້:</span> {{ detail.sections.infrastructure?.usable_classrooms || '—' }}</div>
                    <div class="metric"><span>ສົກຮຽນ:</span> {{ detail.sections.infrastructure?.academic_year || '—' }}</div>
                  </template>
                  <template v-if="sec.key === 'facilities'">
                    <div class="metric"><span>ໄຟຟ້າ:</span> {{ yn(detail.sections.facilities?.has_electricity) }}</div>
                    <div class="metric"><span>ໂທລະສັບ:</span> {{ yn(detail.sections.facilities?.has_phone) }}</div>
                    <div class="metric"><span>ລານກິລາ:</span> {{ yn(detail.sections.facilities?.has_playground) }}</div>
                  </template>
                  <template v-if="sec.key === 'materials'">
                    <div class="metric"><span>ສົກຮຽນ:</span> {{ detail.sections.materials?.academic_year || '—' }}</div>
                    <div class="metric"><span>ອຸປະກອນ:</span> {{ detail.sections.materials?.total_items || '—' }} ລາຍການ</div>
                  </template>
                  <template v-if="sec.key === 'wash_garden'">
                    <div class="metric"><span>ນ້ຳສະອາດ:</span> {{ yn(detail.sections.wash_garden?.has_clean_water) }}</div>
                    <div class="metric"><span>ຫ້ອງນ້ຳ:</span> {{ yn(detail.sections.wash_garden?.has_toilet) }}</div>
                    <div class="metric"><span>ສວນຄົວ:</span> {{ yn(detail.sections.wash_garden?.has_garden) }}</div>
                  </template>
                  <template v-if="sec.key === 'edu_plan'">
                    <div class="metric"><span>ສົກຮຽນ:</span> {{ detail.sections.edu_plan?.academic_year || '—' }}</div>
                    <div class="metric"><span>ເປົ້າໝາຍ:</span> {{ detail.sections.edu_plan?.plan_goal || '—' }}</div>
                  </template>
                </div>
                <div class="sec-foot">
                  <span class="badge-done"><i class="fas fa-check"></i> ມີຂໍ້ມູນແລ້ວ</span>
                  <router-link :to="sec.route" class="sec-link">ເບິ່ງ / ແກ້ໄຂ <i class="fas fa-arrow-right"></i></router-link>
                </div>
              </template>
              <template v-else>
                <div class="sec-no-data">
                  <i class="fas fa-clipboard-list"></i>
                  <p>ຍັງບໍ່ມີຂໍ້ມູນ</p>
                </div>
                <div class="sec-foot">
                  <span class="badge-miss"><i class="fas fa-exclamation"></i> ຂາດຂໍ້ມູນ</span>
                  <router-link :to="sec.route" class="sec-link sec-link-add">ເພີ່ມຂໍ້ມູນ <i class="fas fa-plus"></i></router-link>
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>

      <!-- ── VIEW: MATRIX (multiple schools) ── -->
      <template v-else>
        <!-- Completion bar -->
        <div class="completion-bar-wrap">
          <div v-for="sec in SECTIONS" :key="sec.key" class="comp-bar-item">
            <div class="comp-bar-label">
              <i :class="sec.icon" :style="{ color: sec.color }"></i> {{ sec.shortLabel }}
            </div>
            <div class="comp-bar-track">
              <div class="comp-bar-fill" :style="{ width: sectionPct(sec.key) + '%', background: sec.color }"></div>
            </div>
            <div class="comp-bar-pct">{{ sectionPct(sec.key) }}%</div>
          </div>
        </div>

        <!-- Matrix table -->
        <div class="matrix-card">
          <div class="matrix-title">
            <i class="fas fa-table"></i> ຕາລາງສັງລວມການຄົບຂໍ້ມູນ
            <span class="matrix-count">{{ schools.length }} ໂຮງຮຽນ</span>
            <button class="btn-print no-print" @click="printReport">
              <i class="fas fa-print"></i> ສັ່ງພິມ
            </button>
          </div>
          <div class="table-scroll">
            <table class="matrix-table">
              <thead>
                <tr>
                  <th class="th-fix">#</th>
                  <th class="th-emis th-sortable" @click="setSort('school_id')"
                      :class="{ 'th-active': sortKey === 'school_id' }">
                    ລະຫັດ EMIS <span class="srt-ic">{{ sortIcon('school_id') }}</span>
                  </th>
                  <th class="th-school th-sortable" @click="setSort('school_name_lao')"
                      :class="{ 'th-active': sortKey === 'school_name_lao' }">
                    ຊື່ໂຮງຮຽນ <span class="srt-ic">{{ sortIcon('school_name_lao') }}</span>
                  </th>
                  <th class="th-loc th-sortable" @click="setSort('district_name_lao')"
                      :class="{ 'th-active': sortKey === 'district_name_lao' }">
                    ເມືອງ <span class="srt-ic">{{ sortIcon('district_name_lao') }}</span>
                  </th>
                  <th class="th-stu th-sortable" @click="setSort('students')"
                      :class="{ 'th-active': sortKey === 'students' }">
                    <i class="fas fa-users"></i> ນັກຮຽນ <span class="srt-ic">{{ sortIcon('students') }}</span>
                  </th>
                  <th class="th-tea th-sortable" @click="setSort('teachers')"
                      :class="{ 'th-active': sortKey === 'teachers' }">
                    <i class="fas fa-chalkboard-teacher"></i> ຄູ <span class="srt-ic">{{ sortIcon('teachers') }}</span>
                  </th>
                  <th class="th-room th-sortable" @click="setSort('rooms')"
                      :class="{ 'th-active': sortKey === 'rooms' }">
                    <i class="fas fa-door-open"></i> ຫ້ອງ <span class="srt-ic">{{ sortIcon('rooms') }}</span>
                  </th>
                  <th v-for="sec in SECTIONS" :key="sec.key"
                      class="th-sec th-sortable"
                      :class="{ 'th-active': sortKey === sec.key }"
                      :style="{ borderBottom: '3px solid ' + sec.color }"
                      @click="setSort(sec.key)">
                    <i :class="sec.icon" :style="{ color: sec.color }"></i>
                    <div class="th-sec-label">{{ sec.shortLabel }}</div>
                    <span class="srt-ic">{{ sortIcon(sec.key) }}</span>
                  </th>
                  <th class="th-pct th-sortable" @click="setSort('completionPct')"
                      :class="{ 'th-active': sortKey === 'completionPct' }">
                    ຄົບ % <span class="srt-ic">{{ sortIcon('completionPct') }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(s, i) in sortedSchools" :key="s.school_id"
                    :class="{ 'row-even': i % 2 === 0, 'tr-click': true }"
                    @click="drillToSchool(s)">
                  <td class="td-num">{{ i + 1 }}</td>
                  <td class="td-emis"><span class="emis-badge">{{ s.school_id }}</span></td>
                  <td class="td-name"><span class="school-name-text">{{ s.school_name_lao }}</span></td>
                  <td class="td-loc">{{ s.district_name_lao }}</td>
                  <td class="td-stu">{{ s.students }}</td>
                  <td class="td-tea">{{ s.teachers }}</td>
                  <td class="td-room">{{ s.rooms || 0 }}</td>
                  <td v-for="sec in SECTIONS" :key="sec.key" class="td-check">
                    <span v-if="s.completion[sec.key]" class="chk-done">
                      <i class="fas fa-check-circle"></i>
                    </span>
                    <span v-else class="chk-miss">
                      <i class="fas fa-times-circle"></i>
                    </span>
                  </td>
                  <td class="td-pct">
                    <div class="pct-pill" :class="pctClass(s.completionPct)">
                      {{ s.completionPct }}%
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

    </template>

    <!-- ── EMPTY STATE ── -->
    <div v-else-if="!loading && searched" class="empty-box">
      <i class="fas fa-search fa-3x"></i>
      <p>ບໍ່ພົບຂໍ້ມູນໂຮງຮຽນ</p>
      <span>ລອງປ່ຽນຕົວກອງ ຫຼື ເລືອກເມືອງ / ແຂວງໃໝ່</span>
    </div>

    <div v-else-if="!loading && !searched" class="hint-box">
      <i class="fas fa-filter fa-2x"></i>
      <p>ເລືອກ ແຂວງ / ເມືອງ / ໂຮງຮຽນ ແລ້ວກົດ "ຄົ້ນຫາ"</p>
    </div>

  </div>
</template>

<script>
import api from '@/utils/api';

const LEVELS = [
  { id: 1, name: 'ອະນຸບານ' },
  { id: 2, name: 'ປະຖົມ' },
  { id: 3, name: 'ມັດທະຍົມຕົ້ນ' },
  { id: 4, name: 'ມັດທະຍົມປາຍ' },
];

const SECTIONS = [
  { key: 'profile',        label: 'ຂໍ້ມູນພື້ນຖານ',                    shortLabel: 'ພື້ນຖານ',     icon: 'fas fa-info-circle',      color: '#1565C0', route: '/home/school/info'          },
  { key: 'infrastructure', label: 'ໂຄງລ່າງພື້ນຖານ',                  shortLabel: 'ໂຄງລ່າງ',     icon: 'fas fa-building',          color: '#4527A0', route: '/home/school/infrastructure' },
  { key: 'facilities',     label: 'ສິ່ງອຳນວຍຄວາມສະດວກ',              shortLabel: 'ສິ່ງອຳນວຍ',   icon: 'fas fa-plug',              color: '#00695C', route: '/home/school/facilities'     },
  { key: 'materials',      label: 'ປຶ້ມ ແລະ ອຸປະກອນ',                 shortLabel: 'ອຸປະກອນ',     icon: 'fas fa-book',              color: '#E65100', route: '/home/school/materials'      },
  { key: 'wash_garden',    label: 'ນ້ຳ, ສຸຂະອະນາໄມ, ສວນ, ອາຫານ',    shortLabel: 'ນ້ຳ-ສວນ-ອາຫານ', icon: 'fas fa-leaf',             color: '#2E7D32', route: '/home/school/was-garden'     },
  { key: 'edu_plan',       label: 'ແຜນພັດທະນາການສຶກສາຂັ້ນບ້ານ',       shortLabel: 'ແຜນພັດທະນາ',  icon: 'fas fa-clipboard-list',    color: '#6A1B9A', route: '/home/school/education-plan' },
];

export default {
  name: 'SchoolOverviewPage',
  data() {
    return {
      SECTIONS,
      LEVELS,
      filter: { prov_id: '', district_id: '', school_id: '', academic_year: '', level_id: '' },
      provinces: [], districts: [], schoolList: [], academicYears: [],
      provLoading: false, distLoading: false, schoolLoading: false, accdLoading: false,
      schoolSearch: '', spOpen: false,
      schools: [],
      detail: null,
      loading: false,
      searched: false,
      sortKey: '',
      sortDir: 'asc',
    };
  },
  computed: {
    filteredSchools() {
      const q = this.schoolSearch.toLowerCase().trim();
      if (!q) return this.schoolList;
      return this.schoolList.filter(s =>
        (s.school_name_lao || '').toLowerCase().includes(q) ||
        (s.school_id       || '').toLowerCase().includes(q)
      );
    },
    schoolPickPlaceholder() {
      if (!this.filter.district_id) return 'ເລືອກເມືອງກ່ອນ';
      if (this.filter.school_id && !this.spOpen) {
        const s = this.schoolList.find(x => x.school_id === this.filter.school_id);
        if (s) {
          return s.school_level_lao
            ? `[${s.school_id}] ${s.school_name_lao} (${s.school_level_lao})`
            : `[${s.school_id}] ${s.school_name_lao}`;
        }
        return '-- ທຸກໂຮງຮຽນ --';
      }
      return 'ຄົ້ນຫາ ຊື່ / ລະຫັດໂຮງຮຽນ...';
    },
    totalStudents() { return this.schools.reduce((a, s) => a + (s.students || 0), 0); },
    totalTeachers()  { return this.schools.reduce((a, s) => a + (s.teachers || 0), 0); },
    totalRooms()     { return this.schools.reduce((a, s) => a + (s.rooms    || 0), 0); },
    sortedSchools() {
      if (!this.sortKey) return this.schools;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const key = this.sortKey;
      return [...this.schools].sort((a, b) => {
        let va, vb;
        // section completion keys (boolean)
        if (['profile','infrastructure','facilities','materials','wash_garden','edu_plan'].includes(key)) {
          va = a.completion[key] ? 1 : 0;
          vb = b.completion[key] ? 1 : 0;
        } else {
          va = a[key] ?? '';
          vb = b[key] ?? '';
        }
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb), 'lo') * dir;
      });
    },
  },
  async mounted() {
    await Promise.all([this.fetchProvinces(), this.fetchAcademicYears()]);
    // Auto-load for current user's scope
    const user = JSON.parse(localStorage.getItem('user-info') || '{}');
    if (user.school_id) {
      this.filter.prov_id = user.prov_id || '';
      this.filter.district_id = user.district_id || '';
      this.filter.school_id = user.school_id || '';
      if (user.prov_id) await this.fetchDistricts(user.prov_id);
      if (user.district_id) await this.fetchSchoolList(user.district_id);
      await this.loadOverview();
    }
  },
  methods: {
    printReport() {
      window.print();
    },
    async fetchProvinces() {
      this.provLoading = true;
      try {
        const res = await api.get('/getItems/province/prov_name/prov_id');
        this.provinces = Array.isArray(res.data) ? res.data : [];
      } catch (e) { console.error(e); } finally { this.provLoading = false; }
    },
    async fetchAcademicYears() {
      this.accdLoading = true;
      try {
        const res = await api.get('/accd/all', { params: { limit: 50 } });
        const rows = res.data.rows || [];
        this.academicYears = rows.map(r => r.accd).filter(Boolean).sort().reverse();
      } catch (e) { console.error(e); } finally { this.accdLoading = false; }
    },
    async fetchDistricts(provId) {
      this.distLoading = true;
      try {
        const res = await api.get(`/getItemsCondition/district/district_name_lao/district_id/prov_id/${provId}`);
        this.districts = Array.isArray(res.data) ? res.data : [];
      } catch (e) { console.error(e); } finally { this.distLoading = false; }
    },
    async fetchSchoolList(distId) {
      this.schoolLoading = true;
      try {
        const res = await api.get(`/schools/list-by-district/${distId}`);
        this.schoolList = res.data.rows || [];
      } catch (e) { console.error(e); } finally { this.schoolLoading = false; }
    },
    async onProvChange() {
      this.filter.district_id = ''; this.filter.school_id = '';
      this.districts = []; this.schoolList = [];
      this.schoolSearch = '';
      if (this.filter.prov_id) await this.fetchDistricts(this.filter.prov_id);
    },
    async onDistChange() {
      this.filter.school_id = ''; this.schoolList = []; this.schoolSearch = '';
      if (this.filter.district_id) await this.fetchSchoolList(this.filter.district_id);
    },
    onLevelChange() {
      this.filter.school_id = ''; this.schoolSearch = '';
    },
    pickSchool(s) { this.filter.school_id = s.school_id; this.schoolSearch = ''; this.spOpen = false; },
    clearSchool()  { this.filter.school_id = ''; this.schoolSearch = ''; this.spOpen = false; },
    onSpBlur()     { setTimeout(() => { this.spOpen = false; }, 150); },

    async loadOverview() {
      if (!this.filter.prov_id && !this.filter.district_id && !this.filter.school_id) return;
      this.loading = true; this.searched = true; this.schools = []; this.detail = null;
      try {
        const params = {};
        if (this.filter.prov_id)       params.prov_id       = this.filter.prov_id;
        if (this.filter.district_id)   params.district_id   = this.filter.district_id;
        if (this.filter.school_id)     params.school_id     = this.filter.school_id;
        if (this.filter.academic_year) params.academic_year = this.filter.academic_year;
        if (this.filter.level_id)      params.level_id      = this.filter.level_id;

        const res = await api.get('/dashboard/school-overview', { params });
        this.schools = res.data.code === 1 ? res.data.schools : [];

        if (this.filter.school_id && this.schools.length === 1) {
          await this.loadDetail(this.filter.school_id);
        }
      } catch (e) { console.error(e); } finally { this.loading = false; }
    },

    async loadDetail(schoolId) {
      try {
        const res = await api.get(`/dashboard/school-detail/${schoolId}`);
        if (res.data.code === 1) this.detail = res.data;
      } catch (e) { console.error(e); }
    },

    async drillToSchool(s) {
      this.filter.school_id = s.school_id;
      this.schools = [s];
      this.loading = true;
      await this.loadDetail(s.school_id);
      this.loading = false;
    },

    resetFilter() {
      this.filter = { prov_id: '', district_id: '', school_id: '', academic_year: '', level_id: '' };
      this.districts = []; this.schoolList = []; this.schoolSearch = '';
      this.schools = []; this.detail = null; this.searched = false;
    },

    sectionDoneCount(key) { return this.schools.filter(s => s.completion[key]).length; },
    sectionPct(key) {
      if (!this.schools.length) return 0;
      return Math.round((this.sectionDoneCount(key) / this.schools.length) * 100);
    },
    hasSectionData(key) { return !!(this.detail?.sections?.[key]); },
    pctClass(pct) {
      if (pct === 100) return 'pct-full';
      if (pct >= 67)  return 'pct-high';
      if (pct >= 33)  return 'pct-mid';
      return 'pct-low';
    },
    yn(val) { return val === true || val === 1 || val === 'yes' ? 'ມີ' : val === false || val === 0 ? 'ບໍ່ມີ' : '—'; },

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return '⇅';
      return this.sortDir === 'asc' ? '↑' : '↓';
    },
  },
};
</script>

<style scoped>
.overview-container { animation: fadeIn .3s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; } }

/* ── Header ── */
.header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 6px; }
.header-row h1 { font-size: 1.35rem; color: #311B92; margin: 0 0 4px; display: flex; align-items: center; gap: 8px; }
.header-row p  { color: var(--text3); font-size: 13px; margin: 0; }

/* ── Filter card ── */
.filter-card { background: #fff; border-radius: 14px; padding: 18px 20px; box-shadow: 0 2px 10px rgba(0,0,0,.07); margin-bottom: 18px; }
.filter-row  { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
.filter-group { display: flex; flex-direction: column; gap: 5px; min-width: 160px; flex: 1; }
.filter-group label { font-size: 12px; font-weight: 600; color: var(--text2); display: flex; align-items: center; gap: 5px; }
.filter-accd   { min-width: 140px; flex: 1; }
.filter-level  { min-width: 140px; flex: 1; }
.filter-school { min-width: 220px; flex: 2; }
.sel-wrap { position: relative; }
.sel-wrap select {
  width: 100%; padding: 9px 12px; border: 1px solid var(--border); border-radius: 8px;
  font-size: 13px; background: #fff; cursor: pointer;
}
.sel-wrap select:focus { border-color: #4527A0; outline: none; }
.sel-spin { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: #4527A0; font-size: 12px; }

/* school picker */
.school-pick-wrap { position: relative; display: flex; align-items: center;
                    border: 1px solid var(--border); border-radius: 8px; background: #fff; }
.school-pick-wrap.disabled { background: #f8f9fa; }
.school-pick-wrap:focus-within { border-color: #4527A0; box-shadow: 0 0 0 3px rgba(69,39,160,.1); }
.sp-icon { padding: 0 8px; color: var(--text3); font-size: 13px; flex-shrink: 0; }
.school-pick-wrap input { flex: 1; border: none; outline: none; padding: 9px 4px; font-size: 13px; background: transparent; min-width: 0; }
.school-pick-wrap input:disabled { cursor: not-allowed; color: var(--text3); }
.sp-clear { background: none; border: none; color: #bbb; cursor: pointer; padding: 0 8px; font-size: 13px; }
.sp-clear:hover { color: #e53e3e; }
.sp-arr { padding: 0 8px; color: var(--text3); font-size: 11px; pointer-events: none; }
.sp-list { position: absolute; left: 0; right: 0; top: calc(100% + 4px); z-index: 300;
           background: #fff; border: 1px solid #ddd; border-radius: 10px;
           box-shadow: 0 6px 20px rgba(0,0,0,.1); list-style: none; margin: 0; padding: 4px 0;
           max-height: 200px; overflow-y: auto; }
.sp-list li { display: flex; align-items: flex-start; gap: 7px; padding: 8px 12px; cursor: pointer; font-size: 13px; }
.sp-list li:hover, .sp-list li.active { background: #EDE7F6; color: #4527A0; }
.sp-empty { color: var(--text3); justify-content: center; font-style: italic; }
.sp-empty:hover { background: transparent !important; }
.sp-li-icon { color: #9575CD; font-size: 11px; margin-top: 3px; flex-shrink: 0; }
.sp-school-info { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.sp-school-name { font-size: 13px; font-weight: 500; line-height: 1.3; white-space: normal; }
.sp-school-meta { display: flex; gap: 6px; align-items: center; margin-top: 2px; }
.sp-emis { font-size: 11px; color: #7E57C2; font-weight: 600; background: #EDE7F6; padding: 1px 6px; border-radius: 4px; white-space: nowrap; }
.sp-level { font-size: 11px; color: #666; white-space: nowrap; }

.btn-search {
  display: flex; align-items: center; gap: 7px; padding: 9px 20px;
  background: linear-gradient(135deg, #4527A0, #311B92);
  color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; white-space: nowrap; box-shadow: 0 3px 10px rgba(69,39,160,.3); align-self: flex-end;
}
.btn-search:disabled { opacity: .6; cursor: not-allowed; }
.btn-reset {
  padding: 9px 14px; background: #f1f5f9; border: 1px solid var(--border);
  border-radius: 8px; cursor: pointer; color: var(--text2); align-self: flex-end;
}
.btn-reset:hover { background: #e2e8f0; }

/* ── Loading / hints ── */
.loading-box, .empty-box, .hint-box {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 72px 0; gap: 12px; color: var(--text3); text-align: center;
}
.empty-box span, .hint-box p { font-size: 14px; }

/* ── Stats row ── */
.stats-row { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; }
.stat-card {
  display: flex; align-items: center; gap: 12px;
  background: #fff; border-radius: 12px; padding: 14px 18px;
  box-shadow: 0 2px 8px rgba(0,0,0,.07); flex: 1; min-width: 110px;
}
.stat-icon { width: 36px; height: 36px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
.stat-total .stat-icon { background: #EDE7F6; color: #4527A0; }
.stat-stu   .stat-icon { background: #E3F2FD; color: #1565C0; }
.stat-tea   .stat-icon { background: #FFF3E0; color: #E65100; }
.stat-room  .stat-icon { background: #E8F5EE; color: #1a7f4b; }
.sec-icon   { flex-shrink: 0; }
.stat-num { font-size: 1.3rem; font-weight: 700; color: var(--text1); line-height: 1; }
.stat-of  { font-size: 0.75rem; color: var(--text3); font-weight: 400; }
.stat-lbl { font-size: 11px; color: var(--text3); margin-top: 3px; white-space: nowrap; }

/* ── Completion bars ── */
.completion-bar-wrap { background: #fff; border-radius: 12px; padding: 18px 20px;
                       box-shadow: 0 2px 8px rgba(0,0,0,.07); margin-bottom: 18px; }
.comp-bar-item { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.comp-bar-item:last-child { margin-bottom: 0; }
.comp-bar-label { width: 130px; font-size: 12px; color: var(--text2); display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.comp-bar-track { flex: 1; height: 8px; background: #f0f0f0; border-radius: 4px; overflow: hidden; }
.comp-bar-fill  { height: 100%; border-radius: 4px; transition: width .6s ease; }
.comp-bar-pct   { width: 36px; font-size: 12px; font-weight: 700; color: var(--text2); text-align: right; }

/* ── Matrix ── */
.matrix-card { background: #fff; border-radius: 14px; box-shadow: 0 2px 10px rgba(0,0,0,.07); overflow: hidden; }
.matrix-title { padding: 14px 20px; background: linear-gradient(135deg, #4527A0, #311B92);
                color: #fff; font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
.matrix-count { margin-left: auto; font-size: 12px; font-weight: 400; opacity: .8; }
.btn-print {
  display: flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,.12); color: #fff; border: 1px solid rgba(255,255,255,.4);
  padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: background .2s;
}
.btn-print:hover { background: rgba(255,255,255,.25); }

@media print {
  .filter-card, .no-print { display: none !important; }
}
.table-scroll { overflow-x: auto; }
.matrix-table { width: 100%; border-collapse: collapse; min-width: 860px; }
.matrix-table th { background: #F3EFFF; color: #4527A0; padding: 10px 12px; font-size: 12px; font-weight: 700; text-align: center; white-space: nowrap; border-bottom: 2px solid #E8E0FF; }
.th-sortable { cursor: pointer; user-select: none; transition: background .15s; }
.th-sortable:hover { background: #E8E0FF; }
.th-active { background: #DDD6FE !important; color: #311B92; }
.srt-ic { font-size: 11px; margin-left: 3px; opacity: .7; }
.th-fix    { width: 40px; }
.th-emis   { text-align: left; min-width: 120px; white-space: nowrap; }
.th-school { text-align: left; min-width: 160px; }
.th-loc    { text-align: left; min-width: 100px; }
.th-stu, .th-tea, .th-room { min-width: 60px; }
.th-sec    { min-width: 70px; padding-bottom: 6px; }
.th-sec-label { font-size: 10px; margin-top: 3px; }
.th-pct    { min-width: 70px; }

.matrix-table td { padding: 9px 12px; font-size: 13px; border-bottom: 1px solid #f3f0ff; text-align: center; white-space: nowrap; }
.row-even { background: #fff; }
.matrix-table tr:nth-child(even) { background: #FAFAFE; }
.tr-click { cursor: pointer; transition: background .15s; }
.tr-click:hover { background: #F0EBFF !important; }

.td-num  { color: var(--text3); font-size: 12px; }
.td-name { text-align: left; }
.td-loc  { text-align: left; font-size: 12px; color: var(--text3); }
.td-stu, .td-tea { font-size: 13px; font-weight: 600; color: #1565C0; }
.td-room { font-size: 13px; font-weight: 600; color: #1a7f4b; }

.td-emis { text-align: left; }
.emis-badge { font-size: 11px; font-weight: 700; font-family: monospace; color: #4527A0; background: #EDE7F6; border-radius: 5px; padding: 2px 7px; white-space: nowrap; }
.td-name { text-align: left; }
.school-name-text { font-weight: 600; color: var(--text1); }

.chk-done { color: #1a7f4b; font-size: 16px; }
.chk-miss { color: #e0e0e0; font-size: 16px; }

.pct-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 700; }
.pct-full { background: #E8F5EE; color: #1a7f4b; }
.pct-high { background: #E3F2FD; color: #1565C0; }
.pct-mid  { background: #FFF3E0; color: #E65100; }
.pct-low  { background: #FDEEEE; color: #C62828; }

/* ── Single school detail ── */
.detail-school-bar {
  display: flex; align-items: center; gap: 10px;
  background: linear-gradient(135deg, #4527A0, #311B92);
  color: #fff; border-radius: 12px; padding: 14px 20px; margin-bottom: 16px;
  font-size: 16px; font-weight: 700;
}
.detail-loc { font-size: 12px; font-weight: 400; opacity: .7; margin-left: auto; }

.detail-stats-row { display: flex; gap: 12px; margin-bottom: 18px; flex-wrap: wrap; }
.ds-card { background: #fff; border-radius: 12px; padding: 16px 20px; text-align: center;
           box-shadow: 0 2px 8px rgba(0,0,0,.07); flex: 1; min-width: 100px; }
.ds-card i { font-size: 20px; color: #4527A0; margin-bottom: 6px; display: block; }
.ds-num  { font-size: 1.5rem; font-weight: 700; color: var(--text1); }
.ds-lbl  { font-size: 11px; color: var(--text3); margin-top: 3px; }
.ds-accd { background: #F3EFFF; border: 1px solid #D1C4E9; }
.ds-accd i { color: #6A1B9A; }
.ds-accd-val { font-size: 1rem; color: #4527A0; }

.section-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
@media (max-width: 900px) { .section-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 560px) { .section-grid { grid-template-columns: 1fr; } }

.sec-card { background: #fff; border-radius: 14px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,.08); }
.sec-header {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px; color: #fff; font-size: 13px; font-weight: 700;
  border-radius: 14px 14px 0 0;
}
.sec-status-icon { margin-left: auto; font-size: 15px; }
.sec-body { padding: 14px 16px; }

.sec-metrics { margin-bottom: 12px; }
.metric { display: flex; gap: 6px; font-size: 13px; color: var(--text1); margin-bottom: 5px; }
.metric span { color: var(--text3); font-size: 12px; min-width: 70px; }

.sec-no-data { display: flex; flex-direction: column; align-items: center; padding: 20px 0;
               color: var(--text3); gap: 6px; }
.sec-no-data i { font-size: 28px; opacity: .35; }
.sec-no-data p { margin: 0; font-size: 13px; }

.sec-foot { display: flex; align-items: center; justify-content: space-between; border-top: 1px solid #f5f5f5; padding-top: 10px; margin-top: 8px; }
.badge-done { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; color: #1a7f4b; background: #E8F5EE; padding: 3px 8px; border-radius: 10px; }
.badge-miss { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; color: #C62828; background: #FDEEEE; padding: 3px 8px; border-radius: 10px; }
.sec-link { font-size: 12px; color: #4527A0; text-decoration: none; font-weight: 600; display: flex; align-items: center; gap: 4px; }
.sec-link:hover { text-decoration: underline; }
.sec-link-add { color: #E65100; }
</style>

<style>
@media print {
  .topbar, .sidebar, .sidebar-overlay { display: none !important; }
  .main { margin-left: 0 !important; width: 100% !important; }
}
</style>
